import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { format, parseISO, differenceInDays, isPast, isFuture, isToday, startOfDay } from 'date-fns';
import { useScreenData } from '@/hooks/useScreenData';
import {
  Search,
  X,
  Eye,
  Calendar,
  User,
  Building2,
  FolderKanban,
  Filter,
  Check,
  ChevronDown,
  ChevronUp,
  ArrowUpDown,
  Loader2,
  RefreshCw,
  SlidersHorizontal,
  Settings2,
  RotateCcw,
  GripVertical,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from '@/components/ui/sheet';
import { Switch } from '@/components/ui/switch';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea, ScrollAreaViewport, ScrollAreaScrollbar } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { MultiSelect } from '@/components/ui/multiselect';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  DemoProject,
  ProjectStatus,
  ProjectType,
  ProjectPriority,
  PROJECT_STATUS_CONFIG,
  PROJECT_TYPE_CONFIG,
  PROJECT_PRIORITY_CONFIG,
} from '@/data/mockProjectsData';

import { projectsApi, bookingsApi, Project as ApiProject } from '@/services/api';
import { useCompany } from '@/contexts/CompanyContext';
import { useScheduler } from '@/contexts/SchedulerContext';
import { Project as MockProject, Booking } from '@/types';
import { toast } from 'sonner';
import { HelpButton } from '@/components/help/HelpButton';
import * as integrationsService from '@/services/integrationsService';
import { ProjectDetailPage } from '@/pages/ProjectDetailPage';



// ─── Helpers ────────────────────────────────────────────────────────────────

function formatDate(iso: string) {
  return format(parseISO(iso), 'dd MMM yyyy');
}

function daysRemaining(endDate: string): number {
  return differenceInDays(startOfDay(parseISO(endDate)), startOfDay(new Date()));
}

/**
 * Normalize EBProjects status to the ProjectStatus union used by the UI.
 * Uses the Status string first, then falls back to StatusId if the string is null/empty.
 * EBProjects StatusId mapping (see EBProjects ProjectsController.ResolveStatusName):
 * 1=Planning, 2=In Progress, 3=On Hold, 4=Reopened, 5=Cancelled, 6=Completed, 7=Opportunity.
 */
function normalizeStatus(raw: string | null | undefined, statusId?: number | null): ProjectStatus {
  const s = (raw ?? '').trim().toLowerCase();
  if (s === 'planning' || s === 'planned') return 'planning';
  if (s === 'in progress' || s === 'in-progress' || s === 'inprogress' || s === 'active' || s === 'reopened') return 'in progress';
  if (s === 'on hold' || s === 'on-hold' || s === 'onhold' || s === 'suspended') return 'on hold';
  if (s === 'cancelled' || s === 'canceled' || s === 'archived') return 'cancelled';
  if (s === 'completed' || s === 'complete' || s === 'finished' || s === 'closed') return 'completed';
  if (s === 'estimate' || s === 'opportunity') return 'planning';

  // Fall back to StatusId when Status string is null/empty/unrecognized
  if (statusId != null) {
    switch (statusId) {
      case 1: return 'planning';
      case 2: return 'in progress';
      case 3: return 'on hold';
      case 4: return 'in progress'; // Reopened
      case 5: return 'cancelled';
      case 6: return 'completed';
      case 7: return 'planning'; // Opportunity
    }
  }

  return 'planning';
}

/**
 * Normalize EBProjects type to the ProjectType union.
 * Maps common type strings; defaults to 'time-materials'.
 */
function normalizeType(raw: string | null | undefined): ProjectType {
  const s = (raw ?? '').trim().toLowerCase();
  if (s === 'fixed price' || s === 'fixed-price' || s === 'fixedprice') return 'fixed-price';
  if (s === 'retainer' || s === 'recurring') return 'retainer';
  return 'time-materials';
}

/**
 * Builds a single fallback "Total Costs" budget row from aggregate financials
 * when no Labour/Expense/Revenue breakdown is available.
 * Returns [] if there is truly no financial data.
 */
function buildFallbackBudgetItem(p: ApiProject): import('../data/mockProjectsData').ProjectBudgetItem[] {
  const totalBudget = p.financials?.totalCostsBudget ?? 0;
  const totalActual = p.financials?.totalActualCosts ?? 0;
  if (totalBudget <= 0 && totalActual <= 0) return [];
  return [
    {
      id: `${p.id ?? ''}-total`,
      resource: 'Total Costs',
      role: 'Summary',
      budgetedHours: p.financials?.budgetHours ?? 0,
      actualHours: p.financials?.actualHours ?? 0,
      remainingHours: (p.financials?.budgetHours ?? 0) - (p.financials?.actualHours ?? 0),
      costRate: totalBudget,
      billingRate: totalActual,
    },
  ];
}

/**
 * Builds budget breakdown items from real EBProjects financial data.
 * Uses the `-labour`, `-expense`, `-revenue` ID suffix pattern that
 * ProjectDetailPage already handles for rendering.
 */
function buildEBBudgetItems(p: ApiProject): import('../data/mockProjectsData').ProjectBudgetItem[] {
  const f = p.financials;
  if (!f) return [];
  const hasData = (f.budgetLabour ?? 0) > 0 || (f.budgetExpense ?? 0) > 0 || (f.budgetRevenue ?? 0) > 0;
  if (!hasData) return [];

  const id = p.id ?? '';
  const budgetHours = f.budgetHours ?? 0;
  const actualHours = f.actualHours ?? 0;

  return [
    {
      id: `${id}-labour`,
      resource: 'Labour',
      role: 'Cost Category',
      budgetedHours: budgetHours,
      actualHours: actualHours,
      remainingHours: budgetHours - actualHours,
      costRate: budgetHours > 0 ? (f.budgetLabour ?? 0) / budgetHours : 1,
      billingRate: actualHours > 0 ? (f.actualLabour ?? 0) / actualHours : 1,
    },
    {
      id: `${id}-expense`,
      resource: 'Expenses',
      role: 'Cost Category',
      budgetedHours: 0,
      actualHours: 0,
      remainingHours: 0,
      costRate: f.budgetExpense ?? 0,
      billingRate: f.actualExpense ?? 0,
    },
    {
      id: `${id}-revenue`,
      resource: 'Revenue',
      role: 'Sales Category',
      budgetedHours: 0,
      actualHours: 0,
      remainingHours: 0,
      costRate: f.budgetRevenue ?? 0,
      billingRate: f.actualRevenue ?? 0,
    },
  ];
}

/**
 * Normalize EBProjects priority to the ProjectPriority union.
 * Maps common priority strings; defaults to 'medium'.
 */
function normalizePriority(raw: string | null | undefined): ProjectPriority {
  const s = (raw ?? '').trim().toLowerCase();
  if (s === 'critical' || s === 'urgent') return 'critical';
  if (s === 'high') return 'high';
  if (s === 'low') return 'low';
  return 'medium';
}

/**
 * Maps a unified Project (from the backend /projects endpoint) to the DemoProject
 * interface used by the UI. The backend fetches project data from Access Projects
 * (EBProjects REST) and returns a normalised Project model.
 */
function mapMockProjectToDemoProject(p: MockProject): DemoProject {
  const budgetValue = p.budgetDays ?? 0;
  const consumed = p.consumedDays ?? 0;
  const percentComplete = p.financials?.completionPercent != null
    ? Math.min(100, Math.round(p.financials.completionPercent))
    : budgetValue > 0 ? Math.min(100, Math.round((consumed / budgetValue) * 100)) : 0;
  const budgetHours = p.financials?.budgetHours ?? 0;
  const actualHours = p.financials?.actualHours ?? 0;

  // Derive start/end dates from roleBudgetHours monthly keys when available
  let startDate = new Date().toISOString();
  let endDate = new Date(Date.now() + 90 * 86400000).toISOString();
  if (p.roleBudgetHours?.length) {
    const allKeys = p.roleBudgetHours.flatMap(r => Object.keys(r.monthlyHours)).sort();
    if (allKeys.length) {
      startDate = new Date(allKeys[0] + '-01').toISOString();
      const lastKey = allKeys[allKeys.length - 1];
      const lastDate = new Date(lastKey + '-01');
      lastDate.setMonth(lastDate.getMonth() + 1);
      lastDate.setDate(lastDate.getDate() - 1);
      endDate = lastDate.toISOString();
    }
  }

  // Build budgetItems from roleBudgetHours so ResourceBudgetTab has data to display
  const budgetItems: import('../data/mockProjectsData').ProjectBudgetItem[] =
    (p.roleBudgetHours ?? []).map((r, i) => {
      const totalHours = Object.values(r.monthlyHours).reduce((s, h) => s + h, 0);
      return {
        id: `${String(p.id)}-role-${i}`,
        resource: r.role,
        role: r.role,
        budgetedHours: totalHours,
        actualHours: 0,
        remainingHours: totalHours,
        costRate: 0,
        billingRate: 0,
      };
    });

  // Fall back to financials summary if no roleBudgetHours
  const finalBudgetItems = budgetItems.length > 0
    ? budgetItems
    : (budgetHours > 0 ? [{
        id: `${String(p.id)}-summary`,
        resource: 'All Roles',
        role: 'Summary',
        budgetedHours: budgetHours,
        actualHours: actualHours,
        remainingHours: budgetHours - actualHours,
        costRate: 0,
        billingRate: 0,
      }] : []);

  return {
    id: String(p.id),
    code: p.code ?? '',
    name: p.name,
    description: p.description ?? '',
    status: normalizeStatus(p.status),
    type: 'time-materials',
    priority: 'medium',
    template: '',
    customerName: undefined,
    customerCode: undefined,
    projectManagerId: '',
    projectManagerName: '',
    startDate,
    endDate,
    percentComplete,
    budgetedHours: budgetHours || finalBudgetItems.reduce((s, b) => s + b.budgetedHours, 0),
    actualHours: actualHours,
    budgetedValue: budgetValue,
    actualCost: consumed,
    projectUrl: '',
    team: [],
    milestones: [],
    budgetItems: finalBudgetItems,
    timeEntries: [],
    tags: [],
    region: '',
    roleBudgetHours: p.roleBudgetHours,
  };
}

function mapApiProjectToDemoProject(p: ApiProject): DemoProject {
  // Use actual StartDate/EndDate from the synced project; fall back to createdAt/updatedAt
  const startDate = p.startDate ?? p.createdAt ?? new Date().toISOString();
  const endDate = p.endDate ?? p.updatedAt ?? new Date().toISOString();

  // Budget: prefer live financials over cached budgetDays/consumedDays
  const budgetValue = p.financials?.totalCostsBudget ?? p.budgetDays ?? 0;
  const actualCost = p.financials?.totalActualCosts ?? p.consumedDays ?? 0;

  const budgetHours = p.financials?.budgetHours ?? 0;
  const actualHours = p.financials?.actualHours ?? 0;

  // Completion: prefer financials completionPercent (when > 0), fall back to cost-based or hours-based
  const rawCompletion = p.financials?.completionPercent;
  const percentComplete = (rawCompletion != null && rawCompletion > 0)
    ? Math.min(100, Math.round(rawCompletion))
    : (actualCost > 0 && budgetValue > 0) ? Math.min(100, Math.round((actualCost / budgetValue) * 100))
    : (actualHours > 0 && budgetHours > 0) ? Math.min(100, Math.round((actualHours / budgetHours) * 100))
    : 0;

  // Build real budget items from Labour/Expense/Revenue; fall back to aggregate summary or empty
  const ebBudgetItems = buildEBBudgetItems(p);
  const budgetItems = ebBudgetItems.length > 0 ? ebBudgetItems : buildFallbackBudgetItem(p);

  return {
    id: p.id ?? '',
    externalId: p.externalId ?? undefined,
    code: p.billingCode ?? '',
    name: p.name ?? '',
    description: p.projectDescription ?? '',
    status: normalizeStatus(p.status),
    type: 'time-materials',
    priority: 'medium',
    template: '',
    customerName: p.clientName || undefined,
    customerCode: undefined,
    projectManagerId: '',
    projectManagerName: p.projectManager ?? '',
    startDate,
    endDate,
    percentComplete,
    budgetedHours: budgetHours,
    actualHours: actualHours,
    budgetedValue: budgetValue,
    actualCost,
    projectUrl: '',
    team: [],
    milestones: [],
    budgetItems,
    timeEntries: [],
    tags: [],
    region: '',
    billable: p.billable,
  };
}

// ─── Status filter pills ─────────────────────────────────────────────────────

const ALL_STATUSES: Array<{ value: ProjectStatus | 'all'; label: string }> = [
  { value: 'all',         label: 'All' },
  { value: 'planning',    label: 'Planning' },
  { value: 'in progress', label: 'In Progress' },
  { value: 'on hold',     label: 'On Hold' },
  { value: 'completed',   label: 'Completed' },
  { value: 'cancelled',   label: 'Cancelled' },
];

// ─── Main Page ───────────────────────────────────────────────────────────────

interface ProjectsProps {}

export function Projects({}: ProjectsProps) {
  const { resources, currentMode } = useScreenData('calendar');
  const [modalProject, setModalProject] = useState<DemoProject | null>(null);
  const { mockData, liveData, refreshProjects } = useScheduler();
  const isDemo = currentMode === 'demo';
  const [searchQuery,    setSearchQuery]    = useState('');
  const [statusFilter,   setStatusFilter]   = useState<ProjectStatus[]>([]);
  const [typeFilter,     setTypeFilter]     = useState<ProjectType[]>([]);
  const [priorityFilter, setPriorityFilter] = useState<ProjectPriority[]>([]);
  const [regionFilter,   setRegionFilter]   = useState<string>('all');
  const [pmFilter,       setPmFilter]       = useState<string>('all');

  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);

  // ── Draft filter state (applied only when user clicks "Apply Filters") ──
  const [draftStatusFilter, setDraftStatusFilter] = useState<ProjectStatus[]>([]);
  const [draftTypeFilter, setDraftTypeFilter] = useState<ProjectType[]>([]);
  const [draftPriorityFilter, setDraftPriorityFilter] = useState<ProjectPriority[]>([]);
  const [draftRegionFilter, setDraftRegionFilter] = useState<string>('all');
  const [draftPmFilter, setDraftPmFilter] = useState<string>('all');
  type ProjectSortCol = 'name' | 'code' | 'status' | 'endDate' | 'progress';
  const [sortBy,  setSortBy]  = useState<ProjectSortCol | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  // Column visibility — all on by default
  type ProjectCol = 'code' | 'name' | 'customer' | 'status' | 'billable' | 'pm' | 'due' | 'progress';
  const ALL_PROJECT_COLS: { key: ProjectCol; label: string; description: string; locked?: boolean }[] = [
    { key: 'code',     label: 'Code',     description: 'Unique project code or reference number'           },
    { key: 'name',     label: 'Name',     description: 'Project name and description',   locked: true      },
    { key: 'customer', label: 'Customer', description: 'Client or customer the project belongs to'         },
    { key: 'status',   label: 'Status',   description: 'Current project lifecycle status'                  },
    { key: 'billable', label: 'Billable', description: 'Whether the project is billable to the client'     },
    { key: 'pm',       label: 'PM',       description: 'Assigned project manager'                          },
    { key: 'due',      label: 'Due',      description: 'Project end / due date'                            },
    { key: 'progress', label: 'Progress', description: 'Overall project completion percentage'             },
  ];
  const [visibleCols, setVisibleCols] = useState<Set<ProjectCol>>(
    new Set(ALL_PROJECT_COLS.map((c) => c.key))
  );
  const [isColConfigOpen, setIsColConfigOpen] = useState(false);
  const toggleCol = (key: ProjectCol) =>
    setVisibleCols((prev) => {
      const col = ALL_PROJECT_COLS.find((c) => c.key === key);
      if (col?.locked) return prev;
      const next = new Set(prev);
      if (next.has(key)) { if (next.size > 1) next.delete(key); }
      else next.add(key);
      return next;
    });
  const resetProjectCols = () => setVisibleCols(new Set(ALL_PROJECT_COLS.map((c) => c.key)));

  // ── Multi-select toggle helpers ──────────────────────────────────────────
  function toggleArrayFilter<T>(arr: T[], value: T): T[] {
    return arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];
  }

  // ── Company context ──────────────────────────────────────────────────────
  const { currentCompany, currentCompanyIntegrations } = useCompany();

  // ── Live project data from backend (routed to correct provider per company) ─
  const [liveProjects, setLiveProjects] = useState<DemoProject[]>([]);
  const [isLoadingProjects, setIsLoadingProjects] = useState(true);
  const [projectsError, setProjectsError] = useState<string | null>(null);

  // Monotonic counter bumped by the 'company-changed' custom event so the
  // fetch effect is guaranteed to re-run even when React's dependency
  // comparison doesn't detect a change (e.g. rapid switches, same boolean).
  const [companyRefreshKey, setCompanyRefreshKey] = useState(0);

  // ── Modal bookings state (live data) ────────────────────────────────────
  const [modalBookings, setModalBookings] = useState<Booking[]>([]);

  useEffect(() => {
    if (!modalProject) {
      setModalBookings([]);
      return;
    }
    if (isDemo) {
      setModalBookings(mockData.bookings);
      return;
    }
    let cancelled = false;
    bookingsApi.getByProjectId(modalProject.id)
      .then((bookings) => {
        if (!cancelled) setModalBookings(bookings as unknown as Booking[]);
      })
      .catch((err) => {
        console.warn('Failed to fetch bookings for project:', err);
        if (!cancelled) setModalBookings([]);
      });
    return () => { cancelled = true; };
  }, [modalProject, isDemo, mockData.bookings, liveData.bookings]);

  // ── Sync Now state ──────────────────────────────────────────────────────
  const [isSyncing, setIsSyncing] = useState(false);
  const hasProjectIntegration = !!(currentCompanyIntegrations?.projectIntegration?.externalInstanceId);

  useEffect(() => {
    function onCompanyChanged() {
      setCompanyRefreshKey((k) => k + 1);
    }
    window.addEventListener('company-changed', onCompanyChanged);
    window.addEventListener('company-access-changed', onCompanyChanged);
    return () => {
      window.removeEventListener('company-changed', onCompanyChanged);
      window.removeEventListener('company-access-changed', onCompanyChanged);
    };
  }, []);

  const handleSyncNow = useCallback(async () => {
    if (!currentCompany?.id || isSyncing) return;
    setIsSyncing(true);
    try {
      const result = await integrationsService.syncIntegration(currentCompany.id, 'project');
      if (result.success) {
        toast.success('Sync complete', { description: result.message });
        // Refresh this page's local project list...
        setCompanyRefreshKey((k) => k + 1);
        // ...and the shared SchedulerContext project data so every other page
        // (calendar, utilisation/staffing reports, dashboard) reflects the
        // freshly synced projects when the user navigates there.
        void refreshProjects();
      } else {
        toast.error('Sync failed', { description: result.message });
      }
    } catch {
      toast.error('Sync failed — please try again');
    } finally {
      setIsSyncing(false);
    }
  }, [currentCompany?.id, isSyncing, refreshProjects]);

  // Build a resource name lookup from the resources already loaded in SchedulerContext
  const resourceNameLookup = useMemo(() => {
    const lookup: Record<string, string> = {};
    resources.forEach((r) => {
      if (r.id && r.name) lookup[r.id] = r.name;
    });
    return lookup;
  }, [resources]);

  // Keep ref so the fetch closure always reads the latest value without
  // adding it as an effect dependency (which could cause extra fetches).
  const resourceNameLookupRef = useRef(resourceNameLookup);
  resourceNameLookupRef.current = resourceNameLookup;

  useEffect(() => {
    // No company selected (user has no company access) — show empty list
    if (!currentCompany) {
      setLiveProjects([]);
      setIsLoadingProjects(false);
      return;
    }

    // In demo mode, use mock data (already company-filtered by SchedulerContext)
    if (isDemo) {
      const mapped = mockData.projects.map(mapMockProjectToDemoProject);
      setLiveProjects(mapped);
      setIsLoadingProjects(false);
      return;
    }

    let cancelled = false;

    async function fetchAllProjects() {
      setIsLoadingProjects(true);
      setProjectsError(null);

      try {
        // Call the /projects endpoint with the active company ID so the backend
        // returns only that company's synced projects (or all projects when no
        // integration is configured for the company).
        const apiProjects: ApiProject[] = await projectsApi.getAll(currentCompany?.id);

        if (cancelled) return;

        // Map unified Project model to DemoProject for the UI
        // billable now comes directly from backend merge (ProjectWithFinancials.Billable)
        const mapped = apiProjects.map((p) => mapApiProjectToDemoProject(p));

        setLiveProjects(mapped);
      } catch (err) {
        if (cancelled) return;
        console.warn('Failed to fetch projects from API:', err);
        setLiveProjects([]);
      } finally {
        if (!cancelled) setIsLoadingProjects(false);
      }
    }

    fetchAllProjects();
    return () => { cancelled = true; };
  // currentCompany.id + companyRefreshKey: two independent triggers so the
  // fetch fires on every company switch regardless of React batching timing.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCompany?.id, companyRefreshKey, isDemo, mockData.projects]);

  // ── Fetch all bookings for progress computation ───────────────────────────
  // liveData.bookings only contains bookings created during the current session;
  // pre-existing bookings are loaded per date-range for the calendar. We need
  // all bookings to compute accurate per-project progress in the table.
  const [allFetchedBookings, setAllFetchedBookings] = useState<Booking[]>([]);
  useEffect(() => {
    if (isDemo) return;
    let cancelled = false;
    bookingsApi.getAll()
      .then((bookings) => {
        if (!cancelled) setAllFetchedBookings(bookings as unknown as Booking[]);
      })
      .catch(() => {
        if (!cancelled) setAllFetchedBookings([]);
      });
    return () => { cancelled = true; };
  }, [isDemo, currentCompany?.id, liveData.bookings]);

  const allBookings = isDemo ? mockData.bookings : allFetchedBookings;
  const projectBookingHoursMap = useMemo(() => {
    const map: Record<string, { totalHours: number; completedHours: number }> = {};
    const now = new Date();
    allBookings.forEach((b) => {
      if (b.status === 'cancelled' || !b.projectName) return;
      const key = b.projectName.toLowerCase();
      let hours = 0;
      if (b.durationHours != null && b.durationHours > 0) {
        hours = b.durationHours;
      } else {
        try {
          const bStart = parseISO(b.startTime);
          const bEnd = parseISO(b.endTime);
          const diffMs = bEnd.getTime() - bStart.getTime();
          hours =
            b.durationType === 'full-day' ? Math.max(1, differenceInDays(bEnd, bStart)) * 8 :
            b.durationType === 'half-day-am' || b.durationType === 'half-day-pm' ? 4 :
            Math.max(0, diffMs / (1000 * 60 * 60));
        } catch { /* skip */ }
      }
      if (!map[key]) map[key] = { totalHours: 0, completedHours: 0 };
      map[key].totalHours += hours;
      const isCompleted = b.status === 'completed' || parseISO(b.endTime) < now;
      if (isCompleted) map[key].completedHours += hours;
    });
    return map;
  }, [allBookings]);

  const getEffectiveProgress = useCallback((p: DemoProject): number => {
    if (p.percentComplete > 0) return p.percentComplete;
    const entry = projectBookingHoursMap[p.name?.toLowerCase() ?? ''];
    const bookingHours = entry?.totalHours ?? 0;
    const actualHours = Math.max(p.actualHours, Math.round(bookingHours));
    if (p.budgetedHours > 0) {
      return Math.round((actualHours / p.budgetedHours) * 100);
    }
    // No budget set — derive progress from completed vs total booking hours
    if (entry && entry.totalHours > 0) {
      return Math.round((entry.completedHours / entry.totalHours) * 100);
    }
    return 0;
  }, [projectBookingHoursMap]);

  // ── Derived filter options ─────────────────────────────────────────────────
  const regions = useMemo(
    () => Array.from(new Set(liveProjects.map((p) => p.region).filter(Boolean))).sort(),
    [liveProjects],
  );

  const projectManagers = useMemo((): { id: string; name: string }[] => {
    const seen   = new Set<string>();
    const result: { id: string; name: string }[] = [];
    liveProjects.forEach((p) => {
      if (p.projectManagerId && !seen.has(p.projectManagerId)) {
        seen.add(p.projectManagerId);
        result.push({ id: p.projectManagerId, name: p.projectManagerName });
      }
    });
    return result.sort((a, b) => String(a.name ?? '').localeCompare(String(b.name ?? '')));
  }, [liveProjects]);

  // ── Filtered + sorted list ─────────────────────────────────────────────────
  const filtered = useMemo(() => {
    const q = searchQuery.toLowerCase();
    return liveProjects
      .filter((p) => {
        const matchSearch =
          !q ||
          String(p.name ?? '').toLowerCase().includes(q) ||
          String(p.code ?? '').toLowerCase().includes(q) ||
          (p.customerName ? String(p.customerName).toLowerCase().includes(q) : false) ||
          String(p.projectManagerName ?? '').toLowerCase().includes(q) ||
          p.tags.some((t) => t.toLowerCase().includes(q));
        const matchStatus   = statusFilter.length   === 0 || statusFilter.includes(p.status);
        const matchType     = typeFilter.length     === 0 || typeFilter.includes(p.type);
        const matchPriority = priorityFilter.length === 0 || priorityFilter.includes(p.priority);
        const matchRegion   = regionFilter   === 'all' || p.region            === regionFilter;
        const matchPm       = pmFilter       === 'all' || p.projectManagerId  === pmFilter;
        return matchSearch && matchStatus && matchType && matchPriority && matchRegion && matchPm;
      })
      .sort((a, b) => {
        if (sortBy === null) return 0;
        let cmp = 0;
        switch (sortBy) {
          case 'code':     cmp = String(a.code ?? '').localeCompare(String(b.code ?? ''));     break;
          case 'name':     cmp = String(a.name ?? '').localeCompare(String(b.name ?? ''));     break;
          case 'status':   cmp = String(a.status ?? '').localeCompare(String(b.status ?? '')); break;
          case 'endDate':  cmp = String(a.endDate ?? '').localeCompare(String(b.endDate ?? '')); break;
          case 'progress': cmp = getEffectiveProgress(a) - getEffectiveProgress(b);             break;
        }
        return sortDir === 'asc' ? cmp : -cmp;
      });
  }, [liveProjects, searchQuery, statusFilter, typeFilter, priorityFilter, regionFilter, pmFilter, sortBy, sortDir, getEffectiveProgress]);

  // ── Active filter chips ────────────────────────────────────────────────────
  const activeFilters = useMemo(() => {
    const chips: { key: string; label: string; clear: () => void }[] = [];
    if (searchQuery) chips.push({ key: 'q', label: `"${searchQuery}"`, clear: () => setSearchQuery('') });
    statusFilter.forEach((s) =>
      chips.push({ key: `status-${s}`, label: PROJECT_STATUS_CONFIG[s].label, clear: () => setStatusFilter((prev) => prev.filter((v) => v !== s)) })
    );
    typeFilter.forEach((t) =>
      chips.push({ key: `type-${t}`, label: PROJECT_TYPE_CONFIG[t].label, clear: () => setTypeFilter((prev) => prev.filter((v) => v !== t)) })
    );
    priorityFilter.forEach((p) =>
      chips.push({ key: `pri-${p}`, label: `${PROJECT_PRIORITY_CONFIG[p].label} Priority`, clear: () => setPriorityFilter((prev) => prev.filter((v) => v !== p)) })
    );
    if (regionFilter !== 'all') chips.push({ key: 'region', label: regionFilter, clear: () => setRegionFilter('all') });
    if (pmFilter !== 'all') {
      const pm = projectManagers.find((p) => p.id === pmFilter);
      chips.push({ key: 'pm', label: pm?.name ?? pmFilter, clear: () => setPmFilter('all') });
    }
    return chips;
  }, [searchQuery, statusFilter, typeFilter, priorityFilter, regionFilter, pmFilter, projectManagers]);

  const clearAllFilters = useCallback(() => {
    setSearchQuery('');
    setStatusFilter([]);
    setTypeFilter([]);
    setPriorityFilter([]);
    setRegionFilter('all');
    setPmFilter('all');
  }, []);

  // Count of drawer-only filters (excludes the free-text search)
  const drawerFilterCount = useMemo(() => {
    let count = 0;
    count += statusFilter.length;
    count += typeFilter.length;
    count += priorityFilter.length;
    if (regionFilter !== 'all') count++;
    if (pmFilter     !== 'all') count++;
    return count;
  }, [statusFilter, typeFilter, priorityFilter, regionFilter, pmFilter]);

  // Draft filter count (reflects selections inside the drawer before applying)
  const draftDrawerFilterCount = useMemo(() => {
    let count = 0;
    count += draftStatusFilter.length;
    count += draftTypeFilter.length;
    count += draftPriorityFilter.length;
    if (draftRegionFilter !== 'all') count++;
    if (draftPmFilter     !== 'all') count++;
    return count;
  }, [draftStatusFilter, draftTypeFilter, draftPriorityFilter, draftRegionFilter, draftPmFilter]);

  const handleViewDetails = useCallback((project: DemoProject) => {
    setModalProject(project);
  }, []);

  const toggleSort = (col: ProjectSortCol) => {
    if (sortBy !== col) {
      setSortBy(col);
      setSortDir('asc');
    } else if (sortDir === 'asc') {
      setSortDir('desc');
    } else {
      // third click → reset (no sort)
      setSortBy(null);
      setSortDir('asc');
    }
  };

  const SortIcon = ({ col }: { col: ProjectSortCol }) =>
    sortBy === col ? (
      sortDir === 'asc'
        ? <ChevronUp   className="h-3 w-3 ml-0.5 inline" />
        : <ChevronDown className="h-3 w-3 ml-0.5 inline" />
    ) : (
      <ArrowUpDown className="h-3 w-3 ml-0.5 inline opacity-30" />
    );



  return (
    <div className="h-full overflow-y-auto overflow-x-hidden" data-tour="projects-list">
      <div className="w-full px-6 py-5 space-y-5">

        {/* ── Page Header ─────────────────────────────────────────────── */}
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 shrink-0">
              <FolderKanban className="h-4 w-4 text-primary" />
            </div>
            <div>
              <h1 className="text-xl font-semibold leading-tight">Projects</h1>
              <p className="text-sm text-muted-foreground mt-0.5">
                Manage and track all projects across your organisation
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 shrink-0 pt-1">
            {!isDemo && hasProjectIntegration && (
              <Button
                variant="outline"
                size="sm"
                className="h-8 gap-1.5"
                onClick={handleSyncNow}
                disabled={isSyncing}
              >
                {isSyncing ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <RefreshCw className="h-3.5 w-3.5" />
                )}
                {isSyncing ? 'Syncing…' : 'Sync Now'}
              </Button>
            )}
            <p className="text-sm text-muted-foreground">
              <span className="font-semibold text-foreground">{filtered.length}</span>
              {' / '}
              <span className="font-semibold text-foreground">{liveProjects.length}</span>
            </p>
            <HelpButton />
          </div>
        </div>

        {/* ── Search + Controls Row ────────────────────────────────────── */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
              <Input
                placeholder="Search projects…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 h-9"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>

            {/* Filter drawer trigger */}
            <Button
              variant="outline"
              size="icon"
              className="relative h-9 w-9 shrink-0"
              onClick={() => {
                // Seed drafts from current applied filters when opening the drawer
                setDraftStatusFilter(statusFilter);
                setDraftTypeFilter(typeFilter);
                setDraftPriorityFilter(priorityFilter);
                setDraftRegionFilter(regionFilter);
                setDraftPmFilter(pmFilter);
                setIsFilterDrawerOpen(true);
              }}
              aria-label="Open filters"
            >
              <Filter className="h-4 w-4" />
              {drawerFilterCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground leading-none">
                  {drawerFilterCount}
                </span>
              )}
            </Button>

            {/* Column config */}
            <Button
              variant="outline"
              size="icon"
              className="relative h-9 w-9 shrink-0"
              aria-label="Configure columns"
              onClick={() => setIsColConfigOpen(true)}
            >
              <SlidersHorizontal className="h-4 w-4" />
              {visibleCols.size < ALL_PROJECT_COLS.length && (
                <span className="absolute -top-1.5 -right-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground leading-none">
                  {ALL_PROJECT_COLS.length - visibleCols.size}
                </span>
              )}
            </Button>

          </div>


        </div>

        {/* ── Configure Columns Sheet ──────────────────────────────────── */}
        <Sheet open={isColConfigOpen} onOpenChange={setIsColConfigOpen}>
          <SheetContent side="right" className="w-[440px] sm:max-w-[440px] flex flex-col p-0 gap-0">
            {/* Header */}
            <SheetHeader className="px-6 pt-6 pb-5 border-b shrink-0">
              <div className="flex items-center gap-3 pr-8">
                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 shrink-0">
                  <Settings2 className="h-4 w-4 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <SheetTitle className="text-base font-semibold leading-tight">
                    Customise Columns
                  </SheetTitle>
                  <SheetDescription className="text-xs mt-0.5 leading-relaxed">
                    Toggle columns on or off to personalise the projects table.
                  </SheetDescription>
                </div>
              </div>

              {/* Visible count + reset */}
              <div className="flex items-center justify-between mt-4">
                <div className="flex items-center gap-2">
                  <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-bold tabular-nums shrink-0">
                    {visibleCols.size}
                  </div>
                  <span className="text-sm text-muted-foreground">
                    of {ALL_PROJECT_COLS.length} columns showing
                  </span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 gap-1.5 text-xs text-muted-foreground hover:text-foreground"
                  onClick={resetProjectCols}
                >
                  <RotateCcw className="h-3.5 w-3.5" />
                  Reset defaults
                </Button>
              </div>
            </SheetHeader>

            {/* Column list */}
            <ScrollArea className="flex-1 min-h-0">
              <ScrollAreaViewport>
                <div className="px-4 py-4 space-y-2">
                  {ALL_PROJECT_COLS.map((col) => {
                    const isVisible = visibleCols.has(col.key);
                    return (
                      <div
                        key={col.key}
                        className={[
                          'flex items-center gap-3 px-4 py-3.5 rounded-xl border transition-colors duration-150',
                          isVisible && !col.locked
                            ? 'bg-card border-border hover:border-border/80 hover:bg-accent/30'
                            : col.locked
                              ? 'bg-muted/20 border-border/40'
                              : 'bg-muted/10 border-border/30',
                        ].join(' ')}
                      >
                        {/* Grip handle placeholder */}
                        <GripVertical className={['h-4 w-4 shrink-0', col.locked ? 'text-muted-foreground/20' : 'text-muted-foreground/30'].join(' ')} />

                        {/* Text */}
                        <div className="flex-1 min-w-0">
                          <p className={['text-sm font-medium leading-snug', !isVisible ? 'text-muted-foreground' : 'text-foreground'].join(' ')}>
                            {col.label}
                          </p>
                          <p className="text-xs text-muted-foreground/60 mt-0.5 truncate leading-tight">
                            {col.description}
                          </p>
                        </div>

                        {/* Toggle */}
                        <div className="shrink-0 flex items-center gap-3 ml-auto">
                          {col.locked ? (
                            <span className="text-[10px] font-medium text-muted-foreground/50 italic">Required</span>
                          ) : (
                            <Switch
                              checked={isVisible}
                              onCheckedChange={() => toggleCol(col.key)}
                              aria-label={`${isVisible ? 'Hide' : 'Show'} ${col.label} column`}
                            />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </ScrollAreaViewport>
              <ScrollAreaScrollbar orientation="vertical" />
            </ScrollArea>
          </SheetContent>
        </Sheet>

        {/* ── Filter Drawer ─────────────────────────────────────────────── */}
        <Sheet open={isFilterDrawerOpen} onOpenChange={setIsFilterDrawerOpen}>
          <SheetContent side="right" className="w-[380px] sm:max-w-[380px] flex flex-col p-0 gap-0">

            {/* Header */}
            <SheetHeader className="px-4 pt-4 pb-3 border-b shrink-0 gap-0">
              <div className="flex items-center gap-2">
                <SheetTitle className="text-sm font-semibold">Filters</SheetTitle>
                <Badge
                  variant="secondary"
                  className="flex items-center gap-1 px-2.5 py-0.5 h-6 text-[11px] font-medium rounded-full bg-primary/10 text-primary border-0 hover:bg-primary/10"
                >
                  <Filter className="h-3 w-3" />
                  {draftDrawerFilterCount} {draftDrawerFilterCount === 1 ? 'filter' : 'filters'} active
                </Badge>
              </div>
            </SheetHeader>

            {/* Scrollable filter list */}
            <ScrollArea className="flex-1">
              <ScrollAreaViewport>
                <div className="px-4 py-4 space-y-4">

                  {/* Status */}
                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-foreground">Status</label>
                    <MultiSelect
                      options={ALL_STATUSES
                        .filter(({ value }) => value !== 'all')
                        .map(({ value, label }) => ({ value, label }))}
                      placeholder="All Statuses"
                      value={draftStatusFilter}
                      onValueChange={(v) => setDraftStatusFilter(v as ProjectStatus[])}
                    />
                  </div>

                  <Separator />

                  {/* Type */}
                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-foreground">Type</label>
                    <MultiSelect
                      options={(['fixed-price', 'time-materials', 'retainer'] as ProjectType[]).map((v) => ({
                        value: v,
                        label: PROJECT_TYPE_CONFIG[v].label,
                      }))}
                      placeholder="All Types"
                      value={draftTypeFilter}
                      onValueChange={(v) => setDraftTypeFilter(v as ProjectType[])}
                    />
                  </div>

                  <Separator />

                  {/* Priority */}
                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-foreground">Priority</label>
                    <MultiSelect
                      options={(['critical', 'high', 'medium', 'low'] as ProjectPriority[]).map((v) => ({
                        value: v,
                        label: PROJECT_PRIORITY_CONFIG[v].label,
                      }))}
                      placeholder="All Priorities"
                      value={draftPriorityFilter}
                      onValueChange={(v) => setDraftPriorityFilter(v as ProjectPriority[])}
                    />
                  </div>

                  {regions.length > 0 && (
                    <>
                      <Separator />
                      {/* Region */}
                      <div className="space-y-1.5">
                        <label className="text-xs font-bold text-foreground">Region</label>
                        <Select value={draftRegionFilter} onValueChange={setDraftRegionFilter}>
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="All Regions" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All Regions</SelectItem>
                            {regions.map((r) => (
                              <SelectItem key={r} value={r}>{r}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </>
                  )}

                  {projectManagers.length > 0 && (
                    <>
                      <Separator />
                      {/* Project Manager */}
                      <div className="space-y-1.5">
                        <label className="text-xs font-bold text-foreground">Project Manager</label>
                        <Select value={draftPmFilter} onValueChange={setDraftPmFilter}>
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="All Project Managers" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All Project Managers</SelectItem>
                            {projectManagers.map(({ id, name }) => (
                              <SelectItem key={id} value={id}>{name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </>
                  )}

                </div>
              </ScrollAreaViewport>
              <ScrollAreaScrollbar orientation="vertical" />
            </ScrollArea>

            {/* Footer */}
            <SheetFooter className="px-4 py-3 border-t shrink-0 flex-row items-center justify-between gap-2">
              <Button
                variant="ghost"
                className="h-9 gap-1.5 text-muted-foreground hover:text-foreground px-2"
                onClick={() => {
                  setDraftStatusFilter([]);
                  setDraftTypeFilter([]);
                  setDraftPriorityFilter([]);
                  setDraftRegionFilter('all');
                  setDraftPmFilter('all');
                }}
                disabled={draftDrawerFilterCount === 0}
              >
                <X className="h-3.5 w-3.5" />
                Clear All
              </Button>
              <Button
                className="h-9 gap-1.5 px-5"
                onClick={() => {
                  setStatusFilter(draftStatusFilter);
                  setTypeFilter(draftTypeFilter);
                  setPriorityFilter(draftPriorityFilter);
                  setRegionFilter(draftRegionFilter);
                  setPmFilter(draftPmFilter);
                  setIsFilterDrawerOpen(false);
                }}
              >
                <Check className="h-3.5 w-3.5" />
                Apply Filters
              </Button>
            </SheetFooter>

          </SheetContent>
        </Sheet>

        {/* ── Loading / Error states ────────────────────────────────── */}
        {isLoadingProjects && (
          <div className="py-20 flex flex-col items-center gap-3 text-muted-foreground">
            <Loader2 className="h-8 w-8 animate-spin opacity-40" />
            <p className="text-sm">Loading projects...</p>
          </div>
        )}

        {projectsError && !isLoadingProjects && (
          <div className="py-20 flex flex-col items-center gap-3 text-muted-foreground">
            <FolderKanban className="h-10 w-10 opacity-20" />
            <p className="text-sm text-destructive">{projectsError}</p>
            <button
              onClick={() => window.location.reload()}
              className="text-xs text-primary underline underline-offset-2"
            >
              Retry
            </button>
          </div>
        )}

        {/* ── List View ─────────────────────────────────────────────────── */}
        {!isLoadingProjects && !projectsError && (
          <div className="rounded-lg border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent bg-muted/30">
                  {visibleCols.has('code') && (
                    <TableHead className="w-[90px] cursor-pointer select-none text-xs" onClick={() => toggleSort('code')}>
                      <span className="flex items-center">Code <SortIcon col="code" /></span>
                    </TableHead>
                  )}
                  {visibleCols.has('name') && (
                    <TableHead className="cursor-pointer select-none text-xs" onClick={() => toggleSort('name')}>
                      <span className="flex items-center">Name <SortIcon col="name" /></span>
                    </TableHead>
                  )}
                  {visibleCols.has('customer') && <TableHead className="text-xs">Customer</TableHead>}
                  {visibleCols.has('status') && (
                    <TableHead className="cursor-pointer select-none text-xs" onClick={() => toggleSort('status')}>
                      <span className="flex items-center">Status <SortIcon col="status" /></span>
                    </TableHead>
                  )}
                  {visibleCols.has('billable') && <TableHead className="text-xs w-[90px]">Billable</TableHead>}
                  {visibleCols.has('pm') && <TableHead className="text-xs">PM</TableHead>}
                  {visibleCols.has('due') && (
                    <TableHead className="cursor-pointer select-none text-xs" onClick={() => toggleSort('endDate')}>
                      <span className="flex items-center">Due <SortIcon col="endDate" /></span>
                    </TableHead>
                  )}
                  {visibleCols.has('progress') && (
                    <TableHead className="w-[110px] cursor-pointer select-none text-xs" onClick={() => toggleSort('progress')}>
                      <span className="flex items-center">Progress <SortIcon col="progress" /></span>
                    </TableHead>
                  )}
                  <TableHead className="w-[44px]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={visibleCols.size + 1} className="text-center py-12 text-muted-foreground">
                      <div className="flex flex-col items-center gap-2">
                        <FolderKanban className="h-8 w-8 opacity-20" />
                        <p className="text-sm">No projects found</p>
                        <button onClick={clearAllFilters} className="text-xs text-primary underline underline-offset-2">Clear filters</button>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.map((project) => {
                    const statusCfg = PROJECT_STATUS_CONFIG[project.status];
                    const days      = daysRemaining(project.endDate);
                    const isOverdue = days < 0 && project.status !== 'completed' && project.status !== 'cancelled';
                    return (
                      <TableRow
                        key={project.id}
                        className="cursor-pointer hover:bg-muted/40 even:bg-muted/30 transition-colors"
                        onClick={() => handleViewDetails(project)}
                      >
                        {visibleCols.has('code') && (
                          <TableCell className="text-xs text-muted-foreground py-3">
                            {project.code}
                          </TableCell>
                        )}
                        {visibleCols.has('name') && (
                          <TableCell className="py-3">
                            <p className="font-medium text-sm leading-tight">{project.name}</p>
                          </TableCell>
                        )}
                        {visibleCols.has('customer') && (
                          <TableCell className="py-3 text-sm text-muted-foreground max-w-[140px] truncate">
                            {project.customerName ?? <span className="opacity-30">—</span>}
                          </TableCell>
                        )}
                        {visibleCols.has('status') && (
                          <TableCell className="py-3">
                            <span className="flex items-center gap-1.5 text-xs text-muted-foreground whitespace-nowrap">
                              <span className={`h-2 w-2 rounded-full shrink-0 ${statusCfg.dotClass}`} />
                              {statusCfg.label}
                            </span>
                          </TableCell>
                        )}
                        {visibleCols.has('billable') && (
                          <TableCell className="py-3">
                            {project.billable != null && (
                              <span className={`text-[10px] font-medium rounded-full px-1.5 py-0.5 border inline-flex items-center gap-1 ${project.billable ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950 dark:text-emerald-300 dark:border-emerald-800' : 'bg-muted/40 text-muted-foreground border-border'}`}>
                                <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${project.billable ? 'bg-emerald-500' : 'bg-muted-foreground/40'}`} />
                                {project.billable ? 'Yes' : 'No'}
                              </span>
                            )}
                          </TableCell>
                        )}
                        {visibleCols.has('pm') && (
                          <TableCell className="py-3 text-sm text-muted-foreground whitespace-nowrap">
                            {project.projectManagerName}
                          </TableCell>
                        )}
                        {visibleCols.has('due') && (
                          <TableCell className="py-3">
                            <p className="text-sm whitespace-nowrap">{formatDate(project.endDate)}</p>
                            {isOverdue ? (
                              <p className="text-[10px] text-destructive font-medium">{Math.abs(days)}d overdue</p>
                            ) : project.status !== 'completed' && project.status !== 'cancelled' && days <= 14 ? (
                              <p className="text-[10px] text-amber-600">{days}d remaining</p>
                            ) : null}
                          </TableCell>
                        )}
                        {visibleCols.has('progress') && (
                          <TableCell className="py-3">
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="space-y-1 min-w-[90px]">
                                    <Progress value={getEffectiveProgress(project)} className="h-1.5" />
                                    <p className="text-[10px] text-muted-foreground text-right tabular-nums">{getEffectiveProgress(project)}%</p>
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent side="top">
                                  <p className="text-xs">{getEffectiveProgress(project)}% complete</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </TableCell>
                        )}
                        <TableCell className="py-3">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-muted-foreground hover:text-foreground"
                            onClick={(e) => { e.stopPropagation(); handleViewDetails(project); }}
                          >
                            <Eye className="h-3.5 w-3.5" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        )}



      </div>

      {/* ── Project detail modal ──────────────────────────────────────── */}
      <ProjectDetailPage
        project={modalProject}
        open={modalProject !== null}
        onClose={() => setModalProject(null)}
        allBookings={modalBookings}
        bookingsPreFiltered={!isDemo}
        resources={resources}
      />

    </div>
  );
}
